import { isToday } from 'date-fns';
import React from 'react';
import { Pressable } from 'react-native';
import { withUnistyles } from 'react-native-unistyles';

import { Box, Center, Text } from '@squire/ui';

import { DayStripesOverlay } from '@/components/_shared/DayStripesOverlay';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { DateFormat, formatDate } from '@/utils/dateUtils';

import { styles } from './styles';
import type { CalendarDayFill, CalendarDayProps } from './types';

const StyledPressable = withUnistyles(Pressable);

const CalendarDay = (props: CalendarDayProps) => {
  const {
    date,
    hidden = false,
    selected = false,
    disabled = false,
    isOutOfRange = false,
    isWaitlistOnly = false,
    onPress,
    testID,
  } = props;

  const { t, i18n } = useScreenTranslations('CalendarDay');
  const isTodayDate = isToday(date);

  const dayNumber = formatDate(date, DateFormat.DAY_NUMBER, i18n.language);

  const fill: CalendarDayFill = hidden
    ? 'hidden'
    : isOutOfRange
      ? 'outOfRange'
      : disabled
        ? 'disabled'
        : isWaitlistOnly
          ? 'waitlistOnly'
          : 'available';

  // Hidden (adjacent-month padding) cells must never show selection or
  // today affordances — the underlying date can still equal selectedDate
  // (e.g. today's date rendered as padding when viewing next month).
  const isVisuallySelected = selected && !hidden;

  styles.useVariants({ fill, selected: isVisuallySelected });

  const isPressDisabled = disabled || hidden;

  const getAccessibilityLabel = () => {
    const dateStr = formatDate(
      date,
      DateFormat.USER_FRIENDLY_FULL_DATE,
      i18n.language,
    );
    const parts = [dateStr];
    if (isTodayDate) parts.push(t('accessibility.today'));
    if (isVisuallySelected) parts.push(t('accessibility.selected'));
    if (disabled) parts.push(t('accessibility.notAvailable'));
    return parts.join(', ');
  };

  return (
    <StyledPressable
      style={styles.dayContainer}
      onPress={isPressDisabled ? undefined : onPress}
      disabled={isPressDisabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={getAccessibilityLabel()}
      {...pressedState(isVisuallySelected)}
      aria-disabled={isPressDisabled || undefined}
      accessibilityHint={
        !isPressDisabled ? t('accessibility.tapToSelect') : undefined
      }
    >
      <Center style={styles.indicatorRing}>
        <Center style={styles.dayCircle}>
          {fill === 'disabled' && (
            <DayStripesOverlay
              testID={testID ? `${testID}-stripes` : undefined}
            />
          )}
          <Text variant="mStrong" style={styles.dayText}>
            {dayNumber}
          </Text>

          {isTodayDate && !hidden && (
            <Box
              testID={testID ? `${testID}-today-indicator` : undefined}
              style={styles.todayIndicator}
            />
          )}
        </Center>
      </Center>
    </StyledPressable>
  );
};

CalendarDay.displayName = 'CalendarDay';
export { CalendarDay };
